// The four copycat screens (study of the public Appetite UI fitness previews, light theme). English demo content, fictional
// data copied from the preview. Dashboard → #/accueil, Library → #/programme, Analytics → #/suivi, Exercise → #/seance.
import { useEffect, useState } from "react";
import { ArrowUp, Bell, Check, ChevronRight, Heart, Moon, Play, Pause, Search, SkipBack, SkipForward, Sparkles, X } from "lucide-react";
import { HeartLine, Rings, SleepBars, StatusBar, TabBar, type Route } from "./parts";
import { PHOTOS } from "./photos";

interface ScreenProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

/* ---------------- Dashboard (Today) ---------------- */

const WEEK = [
  { l: "M", n: 28, dot: true }, { l: "T", n: 29, dot: true }, { l: "W", n: 30, dot: true }, { l: "T", n: 1 },
  { l: "F", n: 2 }, { l: "S", n: 3 }, { l: "S", n: 4 },
];

export function Dashboard({ go, onOutOfScope }: ScreenProps) {
  const [day, setDay] = useState(2);
  return <div className="cc-screen">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-today">
        <div><p className="cc-overline">Wednesday, 30 Sep</p><h1 className="cc-title">Today</h1></div>
        <div className="cc-head-actions">
          <button type="button" className="cc-icon-btn" aria-label="Notifications" onClick={() => onOutOfScope("Notifications")}><Bell size={18} strokeWidth={1.8} /></button>
          <img className="cc-avatar" src={PHOTOS.avatar} alt="Profile" />
        </div>
      </header>

      <div className="cc-week" role="tablist" aria-label="Week">
        {WEEK.map((d, i) => <button key={i} type="button" role="tab" aria-selected={day === i} className="cc-day" data-active={day === i || undefined} onClick={() => setDay(i)}>
          <span className="cc-day-l">{d.l}</span><span className="cc-day-n">{d.n}</span><span className="cc-day-dot" data-on={d.dot || undefined} />
        </button>)}
      </div>

      <section className="cc-card cc-rings-card" aria-label="Activity rings">
        <Rings values={[486 / 600, 38 / 45, 9 / 12]} />
        <dl className="cc-ring-stats">
          <div><dt style={{ color: "#1b5cff" }}>Move</dt><dd><b>486</b> /600 kcal</dd></div>
          <div><dt style={{ color: "#22c55e" }}>Exercise</dt><dd><b>38</b> /45 min</dd></div>
          <div><dt style={{ color: "#ff9f0a" }}>Stand</dt><dd><b>9</b> /12 h</dd></div>
        </dl>
      </section>

      <div className="cc-halves">
        <section className="cc-card cc-half"><p className="cc-half-label"><Heart size={13} color="#ef4444" strokeWidth={2} /> Heart rate</p><p className="cc-half-value">62 <small>bpm</small></p><HeartLine /></section>
        <section className="cc-card cc-half"><p className="cc-half-label"><Moon size={13} color="#8b5cf6" strokeWidth={2} /> Sleep</p><p className="cc-half-value">7h 42m</p><SleepBars /></section>
      </div>

      <button type="button" className="cc-upnext" onClick={() => go("seance")} aria-label="Start Legs & Power">
        <img src={PHOTOS.upNext} alt="" />
        <span className="cc-upnext-text"><span className="cc-upnext-kicker">Up next · 19:00</span><span className="cc-upnext-title">Legs &amp; Power</span><span className="cc-upnext-meta">45 min · Advanced · 6 exercises</span></span>
        <span className="cc-upnext-play"><Play size={16} strokeWidth={2} /></span>
      </button>
    </div>
    <TabBar active="home" go={go} onOutOfScope={onOutOfScope} />
  </div>;
}

/* ---------------- Library (Exercise library) ---------------- */

const CHIPS = ["All", "Strength", "Cardio", "Yoga", "Pilates", "Mobility"];
const ROWS = [
  { title: "Deadlift", sub: "Full body · 4 × 6", meta: "Advanced · 10 min", photo: PHOTOS.deadlift },
  { title: "Front Squat", sub: "Legs · 4 × 8", meta: "Intermediate · 12 min", photo: PHOTOS.second },
];

export function Library({ go, onOutOfScope }: ScreenProps) {
  const [chip, setChip] = useState("All");
  const [liked, setLiked] = useState(false);
  return <div className="cc-screen">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-pagehead">
        <h1 className="cc-title">Exercise library</h1>
        <button type="button" className="cc-icon-btn" aria-label="Search" onClick={() => onOutOfScope("Search")}><Search size={18} strokeWidth={1.8} /></button>
      </header>
      <div className="cc-chips" role="tablist" aria-label="Categories">
        {CHIPS.map(c => <button key={c} type="button" role="tab" aria-selected={chip === c} className="cc-chip" data-active={chip === c || undefined} onClick={() => setChip(c)}>{c}</button>)}
      </div>

      <article className="cc-card cc-featured">
        <div className="cc-featured-media">
          <img src={PHOTOS.featured} alt="" />
          <span className="cc-featured-badge"><Sparkles size={11} strokeWidth={2} /> Featured</span>
          <button type="button" className="cc-heart" aria-pressed={liked} aria-label="Save program" onClick={() => setLiked(!liked)}><Heart size={15} strokeWidth={2} fill={liked ? "#ef4444" : "none"} color={liked ? "#ef4444" : "#111"} /></button>
        </div>
        <button type="button" className="cc-featured-body" onClick={() => go("seance")}>
          <span className="cc-featured-title">Legs &amp; Power</span>
          <span className="cc-featured-sub">6-week program · 3 sessions a week</span>
          <span className="cc-featured-meta">Advanced · 45 min</span>
        </button>
      </article>

      <div className="cc-sectionhead"><h2>Strength basics</h2><button type="button" className="cc-seeall" onClick={() => onOutOfScope("See all")}>See all <ChevronRight size={13} strokeWidth={2.2} /></button></div>
      <ul className="cc-rows">
        {ROWS.map(r => <li key={r.title}><button type="button" className="cc-card cc-row" onClick={() => go("seance")}>
          <img src={r.photo} alt="" />
          <span className="cc-row-text"><span className="cc-row-title">{r.title}</span><span className="cc-row-sub">{r.sub}</span><span className="cc-row-meta">{r.meta}</span></span>
        </button></li>)}
      </ul>
    </div>
    <TabBar active="library" go={go} onOutOfScope={onOutOfScope} />
  </div>;
}

/* ---------------- Analytics ---------------- */

/** Stacked bars measured on the preview (heights at 393 px scale): [strength, cardio, mobility]. */
const BARS: { d: string; v: [number, number, number] }[] = [
  { d: "Mon", v: [40, 27, 22] }, { d: "Tue", v: [47, 29, 20] }, { d: "Wed", v: [36, 22, 18] }, { d: "Thu", v: [58, 40, 23] },
  { d: "Fri", v: [47, 27, 22] }, { d: "Sat", v: [65, 43, 29] }, { d: "Sun", v: [58, 32, 23] },
];

export function Analytics({ go, onOutOfScope }: ScreenProps) {
  const [range, setRange] = useState("Week");
  return <div className="cc-screen">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-pagehead cc-pagehead--stack"><h1 className="cc-title">Analytics</h1><p className="cc-overline">Track your progress</p></header>
      <div className="cc-seg" role="tablist" aria-label="Range">
        {["Week", "Month", "Year"].map(r => <button key={r} type="button" role="tab" aria-selected={range === r} data-active={range === r || undefined} onClick={() => setRange(r)}>{r}</button>)}
      </div>

      <section className="cc-card cc-training">
        <p className="cc-small">Training time</p>
        <p className="cc-big">382 min</p>
        <p className="cc-delta"><span className="cc-pill-up"><ArrowUp size={10} strokeWidth={2.6} /> 12.4%</span> vs last week</p>
        {range === "Week"
          ? <div className="cc-bars" aria-label="Training time per day, study data">
              {BARS.map(b => <div key={b.d} className="cc-bar">
                <span className="cc-bar-stack">
                  <span style={{ height: b.v[2], background: "#2dd4bf" }} /><span style={{ height: b.v[1], background: "#8b5cf6" }} /><span style={{ height: b.v[0], background: "#1b5cff" }} />
                </span>
                <span className="cc-bar-d">{b.d}</span>
              </div>)}
            </div>
          : <p className="cc-small cc-empty">Study: only the Week view is drawn.</p>}
        <ul className="cc-legend"><li><i style={{ background: "#1b5cff" }} />Strength</li><li><i style={{ background: "#8b5cf6" }} />Cardio</li><li><i style={{ background: "#2dd4bf" }} />Mobility</li></ul>
      </section>

      <section className="cc-card cc-weight">
        <h2>Weight progress</h2>
        <div className="cc-weight-row">
          <div><p className="cc-small">Current weight</p><p className="cc-weight-v">74.6 <small>kg</small></p></div>
          <div className="cc-right"><p className="cc-small">Target</p><p className="cc-weight-v cc-muted">72.5 <small>kg</small></p></div>
        </div>
        <div className="cc-rail" aria-label="Weight progress, study data"><span style={{ width: "53%" }} /><i style={{ left: "53%" }} /></div>
      </section>
    </div>
    <TabBar active="activity" go={go} onOutOfScope={onOutOfScope} />
  </div>;
}

/* ---------------- Exercise (full screen, no tab bar) ---------------- */

const EXERCISES = [
  { name: "Barbell Back Squats", set: "2 / 4", reps: "12", weight: "84 kg" },
  { name: "Romanian Deadlift", set: "1 / 4", reps: "10", weight: "70 kg" },
  { name: "Bulgarian Split Squat", set: "1 / 3", reps: "10", weight: "20 kg" },
  { name: "Leg Press", set: "1 / 4", reps: "12", weight: "140 kg" },
  { name: "Walking Lunges", set: "1 / 3", reps: "16", weight: "16 kg" },
  { name: "Calf Raises", set: "1 / 3", reps: "15", weight: "40 kg" },
];
const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function Exercise({ go }: ScreenProps) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [seconds, setSeconds] = useState(4 * 60 + 32);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => setSeconds(s => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [playing]);
  const ex = EXERCISES[index]!;
  const step = (d: number) => setIndex(i => (i + d + EXERCISES.length) % EXERCISES.length);

  return <div className="cc-screen cc-exercise">
    <div className="cc-ex-photo"><img src={PHOTOS.exercise} alt="" /></div>
    <StatusBar tone="light" />
    <div className="cc-ex-top">
      <button type="button" className="cc-ex-close" aria-label="Close workout" onClick={() => go("programme")}><X size={18} strokeWidth={2} /></button>
      <span className="cc-ex-name">Legs &amp; Power</span>
      <button type="button" className="cc-ex-check" aria-label="Finish workout" onClick={() => { setPlaying(false); setDone(true); }}><Check size={18} strokeWidth={2.6} /></button>
    </div>
    <div className="cc-ex-body">
      <p className="cc-ex-current">Current exercise</p>
      <h1 className="cc-ex-title">{ex.name}</h1>
      <p className="cc-ex-time">{fmt(seconds)}</p>
      <p className="cc-ex-total">Total time</p>
      <dl className="cc-ex-stats">
        <div><dt>Set</dt><dd>{ex.set}</dd></div>
        <div><dt>Reps</dt><dd>{ex.reps}</dd></div>
        <div><dt>Weight</dt><dd>{ex.weight}</dd></div>
      </dl>
      <div className="cc-ex-controls">
        <button type="button" className="cc-ex-skip" aria-label="Previous exercise" onClick={() => step(-1)}><SkipBack size={18} strokeWidth={2} /></button>
        <button type="button" className="cc-ex-play" aria-label={playing ? "Pause" : "Play"} onClick={() => setPlaying(!playing)}>{playing ? <Pause size={30} fill="#fff" strokeWidth={0} /> : <Play size={30} fill="#fff" strokeWidth={0} style={{ marginLeft: 4 }} />}</button>
        <button type="button" className="cc-ex-skip" aria-label="Next exercise" onClick={() => step(1)}><SkipForward size={18} strokeWidth={2} /></button>
      </div>
    </div>
    {done && <div className="cc-summary" role="dialog" aria-label="Workout complete">
      <div className="cc-summary-card">
        <span className="cc-summary-icon"><Check size={22} strokeWidth={2.6} /></span>
        <h2>Workout complete</h2>
        <p>Legs &amp; Power · {EXERCISES.length} exercises · {fmt(seconds)}</p>
        <button type="button" className="cc-primary" onClick={() => go("accueil")}>Done</button>
      </div>
    </div>}
  </div>;
}
